/* eslint-disable @typescript-eslint/default-param-last */
import _ from 'underscore';
import React from 'react';
import { ScopeContext } from './scope-context';
import { QueryParam } from './query-param';
import {
  ILocation, IHistory, IProcessor, IQueryParams, SaveScopeFunction,
} from '../types';

export interface IDefaultParamProviderProps {
  location?: ILocation;
  history?: IHistory;
  defaults?: IQueryParams | null;
  allowedKeys?: string[];
  paramFormatter?: IProcessor;
  updateOnPathChange?: boolean;
  children: React.ReactNode;
}

export interface IDefaultParamProviderState {
  scope: QueryParam;
  saveScope: SaveScopeFunction;
}

const isScopeChanged = (search: string, scope: QueryParam): boolean => !scope.isEqual(search);

const getNewState = (
  defaults: IQueryParams | null = {},
  location: ILocation | undefined,
  history: IHistory | undefined,
  allowedKeys: string[] = [],
  paramFormatter: IProcessor = _.identity,
  saveScope: SaveScopeFunction,
): IDefaultParamProviderState => {
  const scope = new QueryParam(
    location as ILocation,
    history as IHistory,
    saveScope,
    paramFormatter,
    allowedKeys,
  );
  _.defaults(scope.values, defaults as IQueryParams);

  return {
    scope,
    saveScope,
  };
};

export class DefaultParamProvider extends React.PureComponent<IDefaultParamProviderProps, IDefaultParamProviderState> {
  static defaultProps: Partial<IDefaultParamProviderProps> = {
    location: undefined,
    history: undefined,
    defaults: {},
    allowedKeys: [],
    paramFormatter: _.identity,
    updateOnPathChange: true,
  };

  constructor(props: IDefaultParamProviderProps) {
    super(props);

    const {
      defaults,
      location,
      history,
      allowedKeys,
      paramFormatter,
    } = props;

    this.state = getNewState(
      defaults,
      location,
      history,
      allowedKeys,
      paramFormatter,
      this.saveScope,
    );
  }

  static getDerivedStateFromProps(props: IDefaultParamProviderProps, state: IDefaultParamProviderState): IDefaultParamProviderState | null {
    const {
      defaults,
      location,
      history,
      allowedKeys,
      paramFormatter,
      updateOnPathChange,
    } = props;

    const scopeChanged = isScopeChanged(location!.search, state.scope);
    const pathChanged = location!.pathname !== state.scope.location.pathname;

    if (scopeChanged || (updateOnPathChange && pathChanged)) {
      return getNewState(
        defaults,
        location,
        history,
        allowedKeys,
        paramFormatter,
        state.saveScope,
      );
    }
    return null;
  }

  saveScope: SaveScopeFunction = (scope, pathname = this.props.location!.pathname, extraData) => {
    const {
      defaults,
      location,
      history,
      allowedKeys,
      paramFormatter,
      updateOnPathChange,
    } = this.props;

    const newLocation: ILocation = {
      pathname,
      search: scope.stringify(),
    };

    const scopeChanged = isScopeChanged(location!.search, scope);
    const pathChanged = pathname !== location!.pathname;

    if (scopeChanged || pathChanged) {
      // Case A/B: navigation required — pass extraData into history state.
      // createWebHistory.push merges this with { previousRoute } via _.extend.
      const historyState = extraData !== undefined ? { extraData } : undefined;
      history.push(newLocation, historyState);

      const newState = getNewState(
        defaults,
        newLocation,
        history,
        allowedKeys,
        paramFormatter,
        this.saveScope,
      );

      const scopeChangeAccepted = newState.scope.isEqual(history!.location!.search!);
      const pathChangeAccepted = newState.scope.location.pathname === history!.location!.pathname;

      if ((scopeChanged && scopeChangeAccepted) || (pathChanged && pathChangeAccepted && updateOnPathChange)) {
        this.setState(newState);
        // if changes were needed return `true` to signal that the request could be processed
        return true;
      }

      // if changes were needed return `false` to signal that the request could NOT be processed, e.g. due to navigation rejection
      return false;
    }

    // Case C: no navigation change — update history state in-place without
    // adding a new history entry, then force a re-render so withScope consumers
    // pick up the new extraData via history.location.state.
    if (extraData !== undefined) {
      const currentState = history!.location?.state ?? {};
      if (history!.replace) {
        history!.replace(
          { pathname: location!.pathname, search: location!.search },
          { ...currentState, extraData },
        );
      }
      this.forceUpdate();
    }
    // if no changes were needed return `true` to signal that the request could be processed
    return true;
  };

  render() {
    return (
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      <ScopeContext.Provider value={{ scope: this.state.scope }}>
        {this.props.children}
      </ScopeContext.Provider>
    );
  }
}
